import { format } from 'date-fns';
import React, { useEffect, useMemo } from 'react';
import { FlatList, Platform } from 'react-native';
import styled from 'styled-components/native';

import { useNavigation } from '@react-navigation/core';
import { RouteProp, useRoute } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import ActivityIndicatorScreen from '../../components/ActivityIndicatorScreen';
import { H5, P } from '../../components/Typography';
import { useGetStudyParticipantNotes } from '../../hooks/study/useGetStudyParticipantNotes';
import { StudiesParamList } from '../../navigation/StudiesNavigator';

const StyledView = styled.View`
  flex: 1;
  background-color: white;
`;

const StyledNoteItemView = styled.TouchableOpacity`
  height: 80px;
  background-color: #fafafa;
  align-items: center;
  justify-content: space-between;
  border-bottom-width: 1px;
  border-bottom-color: #d5d5d5;
  padding: 0px 16px 0px 16px;
  flex-direction: row;
`;

const StyledNote = styled(H5)<{ light?: boolean }>`
  ${p =>
    p.light
      ? "font-family: 'WorkSans-Regular';"
      : "font-family: 'WorkSans-Medium';"}
  font-size: 20px;
`;

const StyledH5 = styled(H5)`
  font-size: 24px;
  line-height: 27px;
  margin: 40px 16px 40px 16px;
`;

const TabBarView = styled.View`
  height: 3px;
  width: 100%;
  background-color: #cbcaca;
  position: absolute;
  align-self: center;
  bottom: 0.5px;
  border-radius: 1.5px;
`;

const TabView = styled.TouchableOpacity`
  flex: 1;
`;

const SelectedTabBarView = styled.View<{ selected?: boolean }>`
  ${p => (p.selected ? 'background-color: #8c8c8c;' : '')}
  height: 4px;
  border-radius: 2px;
  width: 100%;
`;

const TabRowView = styled.View`
  width: 100%;
  max-width: 300px;
  flex-direction: row;
  align-self: center;
  margin-bottom: 32px;
  padding-top: 16px;
`;

const StyledTextContainer = styled.View`
  flex: 1;
  align-items: center;
  justify-content: center;
`;

export const noteDateFormat = 'MMMM do, yyyy';

type NotesGroup = {
  date: Date;
  count: number;
};

const NotebookListScreen: React.FC = () => {
  const route = useRoute<RouteProp<StudiesParamList, 'StudyNotebook'>>();
  const studyParticipantId = route?.params?.studyParticipantId;
  const navigation = useNavigation<StackNavigationProp<any>>();
  const {
    studyParticipantNotes,
    loadingStudyParticipantNotes,
    getStudyParticipantNotes
  } = useGetStudyParticipantNotes(studyParticipantId || 0);
  useEffect(() => {
    if (studyParticipantId) getStudyParticipantNotes();
  }, [studyParticipantId, getStudyParticipantNotes]);

  const displayedNotesGroup = useMemo(() => {
    if (!studyParticipantNotes) return [];
    return studyParticipantNotes
      .reduce((notesGroup, note) => {
        const index = notesGroup?.findIndex(n => {
          return (
            format(n.date, noteDateFormat) ===
            format(note.intendedStartDate, noteDateFormat)
          );
        });
        if (index > -1) {
          notesGroup[index].count += 1;
        } else {
          notesGroup.push({ date: note.intendedStartDate, count: 1 });
        }
        return notesGroup;
      }, [] as NotesGroup[])
      .sort((a, b) => {
        if (a.date > b.date) return -1;
        return 1;
      });
  }, [studyParticipantNotes]);

  return loadingStudyParticipantNotes ? (
    <ActivityIndicatorScreen />
  ) : (
    <StyledView>
      <StyledH5 color='dark'>Notes ✏️</StyledH5>
      {displayedNotesGroup.length > 0 ? (
        <FlatList
          data={displayedNotesGroup}
          keyExtractor={item => item.date.toDateString()}
          renderItem={({ item, index }) => (
            <StyledNoteItemView
              style={
                index === 0 && {
                  borderTopWidth: 1,
                  borderTopColor: '#d5d5d5'
                }
              }
              onPress={() => {
                navigation.navigate('StudiesNav', {
                  screen: 'StudyNotebookDetail',
                  params: { date: item.date.toString(), studyParticipantId }
                });
              }}
            >
              <StyledNote color='dark'>
                {format(new Date(item.date), 'MMMM do, yyyy')}
              </StyledNote>
              <StyledNote color='dark' light>
                {item.count}
              </StyledNote>
            </StyledNoteItemView>
          )}
        />
      ) : (
        <StyledTextContainer>
          <P>No Notes Found</P>
        </StyledTextContainer>
      )}
      {/* <TabRowView>
        <TabBarView />
        <TabView>
          <TabBarLabel color='dark' center>
            RECENT
          </TabBarLabel>
          <SelectedTabBarView selected />
        </TabView>
        <TabView>
          <TabBarLabel color='dark' center>
            QUESTION
          </TabBarLabel>
          <SelectedTabBarView />
        </TabView>
      </TabRowView> */}
    </StyledView>
  );
};

export default NotebookListScreen;
